<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{ $t("bikeAddress.pageTitle") }}</app-top-bar>
    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'" class="text-on-surface-variant">
      <v-container v-if="!showComplete">
        <h1 class="page-title mb-6">{{ $t("bikeAddress.mainTitle") }}</h1>

        <v-form ref="addressForm" v-model="formValid" lazy-validation>
          <v-text-field
            v-model="address.postalCode"
            v-mask="['#####-###']"
            class="mb-1 text-on-surface-variant"
            dense
            :label="$t('bikeAddress.cepLabel')"
            :loading="isLoading"
            required
            :rules="rules.postalCode"
            type="tel"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-text-field
            v-if="addressRetrieved"
            v-model="address.street"
            class="mb-1"
            dense
            :label="$t('bikeAddress.streetLabel')"
            required
            :rules="rules.street"
            type="text"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-text-field
            v-if="addressRetrieved"
            v-model="address.number"
            class="mb-1"
            dense
            :label="$t('bikeAddress.numberLabel')"
            required
            :rules="rules.number"
            type="text"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-text-field
            v-if="addressRetrieved"
            v-model="address.complement"
            class="mb-1"
            dense
            :label="$t('bikeAddress.complementLabel')"
            type="text"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-text-field
            v-if="addressRetrieved"
            v-model="address.neighborhood"
            class="mb-1"
            dense
            :label="$t('bikeAddress.neighborhoodLabel')"
            required
            :rules="rules.neighborhood"
            type="text"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-text-field
            v-if="addressRetrieved"
            v-model="address.city"
            class="mb-1"
            dense
            :disabled="!addressError"
            :label="$t('bikeAddress.cityLabel')"
            required
            :rules="rules.city"
            type="text"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
          <v-select
            v-if="addressRetrieved"
            v-model="address.state"
            class="mb-1"
            dense
            :disabled="!addressError"
            :items="states"
            :label="$t('bikeAddress.stateLabel')"
            required
            :rules="rules.state"
            variant="underlined"
            color="primary"
            background-color="surface"
          />
        </v-form>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer>
      <continue-footer-button
        v-if="!showComplete"
        color="primary"
        :loading="isAdvancing"
        :disabled="!isAddressFormValid"
        @click="advance"
      >
        {{ $t("bikeAddress.continueButton") }}
      </continue-footer-button>
      <continue-footer-button
        v-if="showComplete"
        color="primary"
        :loading="isAdvancing"
        @click="advanceFlow"
      />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive, ref, useTemplateRef, watch} from "vue";
import { useI18n } from "vue-i18n";
import { mask as vMask } from "vue-the-mask";

import { usePageBackground } from "@/composables/usePageBackground";
import { useStepFlow } from "@/composables/useStepFlow";
import {useUtils} from "@/composables/useUtils";
import { useApiStore } from "@/stores/api";
import { useAppStore } from "@/stores/app";
import type { Address } from "@/types/api";

const { t } = useI18n();

const util = useUtils();
const api = useApiStore();
const app = useAppStore();
const { mainStyle, hasBackgroundImage } = usePageBackground();

const isComplete = computed<boolean>(() => api.isItemComplete("address"));

const isLoading = ref<boolean>(false);
const addressError = ref<boolean>(false);
const addressRetrieved = ref<boolean>(false);
const pathPage = '/address';

const { isAdvancing, showComplete, advance: advanceFlow } = useStepFlow(pathPage, () => isComplete.value);
const address = reactive<Address>({
  postalCode: api.address?.postalCode ?? null,
  street: api.address?.street ?? null,
  number: api.address?.number ?? null,
  complement: api.address?.complement ?? null,
  neighborhood: api.address?.neighborhood ?? null,
  city: api.address?.city ?? null,
  state: api.address?.state ?? null,
});

const formValid = ref<boolean>(false);
const addressFormRef = useTemplateRef("addressForm");
const isAddressFormValid = computed<boolean>(
  () => (formValid.value && addressRetrieved.value) || false
);

watch(
  () => address.postalCode,
  (postalCode) => {
    if (postalCode == null || postalCode.length < 9) {
      addressRetrieved.value = false;
      addressError.value = false;
      return;
    }
    onPostalCodeInput();
  }
);

function onPostalCodeInput() {
  if (address.postalCode == null || address.postalCode.length < 9) {
    addressRetrieved.value = false;
    return;
  }

  isLoading.value = true;
  api
    .getAddress(address.postalCode)
    .then((receivedAddress) => {
      address.street = receivedAddress.street;
      address.complement = receivedAddress.complement;
      address.neighborhood = receivedAddress.neighborhood;
      address.city = receivedAddress.city;
      address.state = receivedAddress.state;

      addressError.value = false;
      addressRetrieved.value = true;
    })
    .catch((e) => {
      addressError.value = true;
      addressRetrieved.value = true;
    })
    .finally(() => {
      isLoading.value = false;
    });
}

async function advance() {
  const { valid } = await addressFormRef.value.validate();
  if (!valid) {
    return;
  }
  try {
    await advanceFlow(async () => {
      await api.setAddress({ ...address });
    });
  } catch (error) {
    console.error(error);
    util.noticeError(error);
  }
}

const states = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const rules = {
  postalCode: [(v) => !!v || t("bikeAddress.requiredField")],
  street: [(v) => !!v || t("bikeAddress.requiredField")],
  number: [(v) => !!v || t("bikeAddress.requiredNumberField")],
  neighborhood: [(v) => !!v || t("bikeAddress.requiredField")],
  city: [(v) => !!v || t("bikeAddress.requiredField")],
  state: [(v) => !!v || t("bikeAddress.requiredField")],
};

onMounted(() => {
  if (
    api.address &&
    Object.keys(api.address).every((key) => api.address[key] != null)
  ) {
    addressRetrieved.value = true;
  }
});
</script>

<style scoped>
.cep-container {
  padding-bottom: 0px;
}
</style>
